import { Controller } from "@hotwired/stimulus"

// Drives the per-locale "Traduzir" button on the feature form.
//
// Bruno's 2026-05-18 review (~50:36 + ~51:03):
//
//   1. Button must work in BOTH Create and Edit flows. Same controller
//      handles both — the underlying form structure is identical
//      (translations[<locale>][name] + feat_desc_<locale> hidden input
//      mirrored by a Lexical editor).
//
//   2. If the target locale is blank, just paste the translation. If
//      it has text, ask for confirmation before overwriting — the
//      editor may have written something they don't want to lose.
//
// Each locale panel mounts one instance scoped to itself. The data
// attributes tell the controller WHICH locale is the target (this
// panel) and which is the source (the other one).
export default class extends Controller {
  static values = {
    targetLocale: String,
    sourceLocale: String,
    featureId:    String,
    endpoint:     { type: String, default: "/admin/features/ai_translate" },
    confirmText:  { type: String, default: "O conteúdo atual será substituído. Continuar?" },
    emptyText:    { type: String, default: "Não há conteúdo no outro idioma para traduzir." }
  }
  static targets = ["btn"]

  connect() {
    this._abortController = null
  }

  disconnect() {
    if (this._abortController) this._abortController.abort()
  }

  async translate(event) {
    event?.preventDefault?.()
    if (this.busy) return
    this.busy = true

    try {
      const source = this._readLocale(this.sourceLocaleValue)
      if (!source.name && !this._stripHtml(source.description)) {
        alert(this.emptyTextValue)
        return
      }

      const target = this._readLocale(this.targetLocaleValue)
      const targetHasContent = target.name.trim().length > 0 || this._stripHtml(target.description).trim().length > 0
      if (targetHasContent && !confirm(this.confirmTextValue)) return

      this._setBtnBusy(true)
      if (this._abortController) this._abortController.abort()
      this._abortController = new AbortController()

      const csrf = document.querySelector("meta[name='csrf-token']")?.content
      const body = new FormData()
      body.append("source_locale", this.sourceLocaleValue)
      body.append("target_locale", this.targetLocaleValue)
      body.append("name", source.name)
      body.append("description", source.description)
      if (this.featureIdValue) body.append("feature_id", this.featureIdValue)

      const response = await fetch(this.endpointValue, {
        method: "POST",
        body,
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": csrf || ""
        },
        credentials: "same-origin",
        signal: this._abortController.signal
      })
      const data = await response.json()
      if (!response.ok) {
        alert(data.error || `${response.status}`)
        return
      }

      this._writeLocale(this.targetLocaleValue, data.name, data.description)
    } catch (err) {
      if (err.name === "AbortError") return
      alert(err.message)
    } finally {
      this._setBtnBusy(false)
      this._abortController = null
      this.busy = false
    }
  }

  _readLocale(locale) {
    const nameInput = this._nameInput(locale)
    const descHidden = this._descriptionInput(locale)
    const hiddenDescription = descHidden?.value || ""
    const liveDescription = this._readEditorHtml(locale)
    const description = this._stripHtml(liveDescription).trim() ? liveDescription : hiddenDescription

    if (descHidden && description && descHidden.value !== description) {
      descHidden.value = description
    }

    return {
      name: nameInput?.value || "",
      description: description || ""
    }
  }

  _writeLocale(locale, name, description) {
    const nameInput = this._nameInput(locale)
    if (nameInput) {
      nameInput.value = name || ""
      nameInput.dispatchEvent(new Event("input", { bubbles: true }))
    }

    const descHidden = this._descriptionInput(locale)
    if (descHidden) descHidden.value = description || ""

    // Push HTML into the Lexical editor controller bound to this input
    // (selector matches the rich_editor partial's wrapper attribute).
    const ctrl = this._editorController(locale)
    if (ctrl && typeof ctrl.load === "function") ctrl.load(description || "")
  }

  _nameInput(locale) {
    return document.querySelector(`input[name="translations[${locale}][name]"]`)
  }

  _descriptionInput(locale) {
    return document.getElementById(`feat_desc_${locale}`)
  }

  _readEditorHtml(locale) {
    const ctrl = this._editorController(locale)
    if (ctrl && typeof ctrl.html === "function") return ctrl.html()

    const surface = this._editorElement(locale)?.querySelector('[data-lexical-editor-target="surface"]')
    return surface?.innerHTML || ""
  }

  _editorController(locale) {
    const editorEl = this._editorElement(locale)
    if (!editorEl) return null
    return this.application.getControllerForElementAndIdentifier(editorEl, "lexical-editor")
  }

  _editorElement(locale) {
    return document.querySelector(`[data-rich-editor-for="feat_desc_${locale}"]`)
  }

  _stripHtml(html) {
    if (!html) return ""
    const tmp = document.createElement("div")
    tmp.innerHTML = html
    return tmp.textContent || ""
  }

  _setBtnBusy(busy) {
    if (!this.hasBtnTarget) return
    this.btnTarget.disabled = busy
    this.btnTarget.classList.toggle("loading", busy)
  }
}
